import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell, BellOff, Phone, Check, Wifi, WifiOff, Volume2, VolumeX, Send, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "../../lib/api";
import { formatKobo } from "../../lib/format";

const BASE_TITLE = "Sapore Eats — Breakfast & Catering";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function minutesAgo(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m ago`;
}

const isToday = (iso) => iso && new Date(iso).toDateString() === new Date().toDateString();

export default function AlertsDashboard() {
  const [armed, setArmed] = useState(() => sessionStorage.getItem("sapore_alerts_armed") === "1");
  const [orders, setOrders] = useState([]);
  const [connection, setConnection] = useState("polling");
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [testResults, setTestResults] = useState(null);
  const [allergyOnly, setAllergyOnly] = useState(false);
  const [, forceTick] = useState(0);

  const seenRef = useRef(new Set());
  const audioRef = useRef(null);
  const sseRef = useRef(null);
  const wakeRef = useRef(null);

  const soundEnabled = settings?.sound_enabled ?? true;
  const repeatSeconds = settings?.repeat_seconds ?? 45;

  const pending = orders.filter((o) => o.alert_status === "pending");
  const inProgress = orders.filter((o) => o.alert_status === "acknowledged" && !["Delivered", "Cancelled", "Refunded"].includes(o.status));
  const completed = orders.filter((o) => ["Delivered", "Cancelled", "Refunded"].includes(o.status) && isToday(o.created_at));
  const pendingCount = pending.length;
  const pendingRef = useRef(0);
  pendingRef.current = pendingCount;
  const af = (arr) => (allergyOnly ? arr.filter((o) => o.allergy_flag) : arr);

  // ---------- sound ----------
  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = audioRef.current;
      if (!ctx) return;
      const now = ctx.currentTime;
      [880, 1245].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = freq;
        const start = now + i * 0.22;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.5, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.22);
      });
    } catch (err) { console.debug("alerts: non-critical failure", err); }
  }, [soundEnabled]);

  const fireAlert = useCallback((order) => {
    playBeep();
    if (navigator.vibrate) navigator.vibrate([300, 150, 300]);
    if ("Notification" in window && Notification.permission === "granted") {
      navigator.serviceWorker?.ready?.then((reg) => {
        reg.showNotification(`New order ${order.reference}`, {
          body: `${formatKobo(order.total)} · ${order.customer?.name || ""}`,
          tag: `order-${order.reference}`,
          renotify: true,
          requireInteraction: true,
          data: { url: "/admin/alerts" },
          icon: "/assets/sapore-favicon.png",
        });
      }).catch(() => {});
    }
  }, [playBeep]);

  // ---------- data merge ----------
  const mergeOrders = useCallback((incoming, { initial = false } = {}) => {
    const list = Array.isArray(incoming) ? incoming : [incoming];
    setOrders((prev) => {
      const map = new Map(prev.map((o) => [o.id || o.reference, o]));
      list.forEach((o) => { if (o && (o.id || o.reference)) map.set(o.id || o.reference, { ...map.get(o.id || o.reference), ...o }); });
      return Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    list.forEach((o) => {
      const id = o?.id || o?.reference;
      if (!id) return;
      if (o.alert_status === "pending" && !seenRef.current.has(id)) {
        seenRef.current.add(id);
        if (!initial) fireAlert(o);
      }
    });
  }, [fireAlert]);

  const loadOrders = useCallback(async (initial = false) => {
    try {
      const { data } = await api.get("/admin/alerts/orders");
      mergeOrders(data, { initial });
    } catch (err) { console.debug("alerts: non-critical failure", err); }
  }, [mergeOrders]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/alert-settings");
      setSettings(data);
    } catch (err) { console.debug("alerts: non-critical failure", err); }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notification-log?limit=20");
      setLog(data);
    } catch (err) { console.debug("alerts: non-critical failure", err); }
  }, []);

  // ---------- initial load + polling ----------
  useEffect(() => {
    loadSettings();
    loadOrders(true);
    loadLog();
    const poll = setInterval(() => loadOrders(false), 20000);
    const ticker = setInterval(() => forceTick((t) => t + 1), 30000);
    return () => { clearInterval(poll); clearInterval(ticker); };
  }, [loadOrders, loadSettings, loadLog]);

  // ---------- SSE ----------
  useEffect(() => {
    let es;
    try {
      es = new EventSource(`${API}/admin/alerts/stream`, { withCredentials: true });
      sseRef.current = es;
      es.onopen = () => setConnection("live");
      es.addEventListener("order", (ev) => {
        try { mergeOrders(JSON.parse(ev.data)); } catch (err) { console.debug("alerts: bad SSE payload", err); }
      });
      es.onerror = () => setConnection("polling");
    } catch (_) { setConnection("polling"); }
    return () => { if (es) es.close(); };
  }, [mergeOrders]);

  // ---------- repeat loop ----------
  useEffect(() => {
    if (!armed) return;
    const id = setInterval(() => {
      if (pendingRef.current > 0) {
        playBeep();
        if (navigator.vibrate) navigator.vibrate([300, 150, 300]);
      }
    }, repeatSeconds * 1000);
    return () => clearInterval(id);
  }, [armed, repeatSeconds, playBeep]);

  // ---------- title flash ----------
  useEffect(() => {
    let flip = false;
    const id = setInterval(() => {
      if (pendingRef.current > 0) {
        flip = !flip;
        document.title = flip ? `(${pendingRef.current}) NEW ORDER — Sapore` : BASE_TITLE;
      } else {
        document.title = BASE_TITLE;
      }
    }, 1000);
    return () => { clearInterval(id); document.title = BASE_TITLE; };
  }, []);

  // ---------- arming ----------
  const enableAlerts = async () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      await ctx.resume();
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(ctx.destination); src.start(0);
      audioRef.current = ctx;
    } catch (err) { console.debug("alerts: non-critical failure", err); }

    try {
      if ("Notification" in window) await Notification.requestPermission();
    } catch (err) { console.debug("alerts: non-critical failure", err); }

    try {
      if ("serviceWorker" in navigator && "PushManager" in window) {
        const reg = await navigator.serviceWorker.register("/sw.js");
        await navigator.serviceWorker.ready;
        const { data } = await api.get("/admin/vapid-public-key");
        if (data.public_key) {
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.public_key),
          });
          const json = sub.toJSON();
          await api.post("/admin/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
        }
      }
    } catch (e) {
      console.warn("Web Push subscribe failed:", e);
    }

    try {
      if ("wakeLock" in navigator) wakeRef.current = await navigator.wakeLock.request("screen");
    } catch (err) { console.debug("alerts: non-critical failure", err); }

    sessionStorage.setItem("sapore_alerts_armed", "1");
    setArmed(true);
    playBeep();
    toast.success("Alerts enabled");
  };

  const acknowledge = async (order) => {
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/acknowledge`);
      mergeOrders(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---------- settings mutations ----------
  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/admin/alert-settings", patch);
      setSettings(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleChannel = (name) => {
    const channels = JSON.parse(JSON.stringify(settings.channels));
    channels[name].enabled = !channels[name].enabled;
    saveSettings({ channels });
  };
  const sendTest = async () => {
    setTestResults("loading");
    try {
      const { data } = await api.post("/admin/test-alert");
      setTestResults(data.results);
      loadLog();
    } catch (e) { setTestResults([{ driver: "error", status: "failed", error: formatApiError(e.response?.data?.detail) }]); }
  };

  return (
    <div className="sapore-events" style={{ background: "transparent", overflow: "visible" }}>
      {/* Alert banner */}
      {armed && pendingCount > 0 && (
        <button
          data-testid="alert-banner"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white font-bold text-center py-3 animate-pulse"
        >
          {pendingCount} {pendingCount === 1 ? "order" : "orders"} waiting — tap to acknowledge
        </button>
      )}

      <div className={armed && pendingCount > 0 ? "pt-12" : ""}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="font-serif text-3xl font-bold text-sapore-purple">Order Alerts</h1>
          <div className="flex items-center gap-2">
            <button data-testid="allergy-filter" onClick={() => setAllergyOnly((v) => !v)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${allergyOnly ? "bg-red-600 text-white border-red-600" : "bg-white text-red-600 border-red-300"}`}>
              ⚠ Allergy orders
            </button>
            <span data-testid="connection-state" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${connection === "live" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
              {connection === "live" ? <Wifi size={14} /> : <WifiOff size={14} />}
              {connection === "live" ? "Live" : "Reconnecting — polling"}
            </span>
            <button onClick={() => loadOrders(false)} className="p-2 rounded-full border border-sapore-border text-sapore-purple" data-testid="refresh-orders"><RefreshCw size={16} /></button>
          </div>
        </div>

        {/* Arming gate */}
        {!armed && (
          <div data-testid="arming-gate" className="mb-6">
            <div className="bg-amber-100 text-amber-800 rounded-xl px-4 py-3 mb-3 font-medium">Alerts are off — tap Enable alerts.</div>
            <button data-testid="enable-alerts-button" onClick={enableAlerts} className="w-full sm:w-auto flex items-center justify-center gap-2 bg-sapore-purple text-sapore-cream font-semibold px-8 py-4 rounded-full text-lg hover:bg-sapore-purpleDark transition-colors" style={{ minHeight: 44 }}>
              <Bell size={20} /> Enable alerts
            </button>
          </div>
        )}

        {/* Board */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <OrderColumn title="New" testid="col-new" accent count={af(pending).length} orders={af(pending)} onAck={acknowledge} showAck />
          <OrderColumn title="In progress" testid="col-inprogress" count={af(inProgress).length} orders={af(inProgress)} onAck={acknowledge} />
          <OrderColumn title="Completed today" testid="col-completed" count={af(completed).length} orders={af(completed)} muted />
        </div>

        {/* Settings */}
        {settings && (
          <div className="mt-10 bg-white rounded-2xl border border-sapore-border p-6" data-testid="alert-settings-panel">
            <h2 className="font-serif text-2xl text-sapore-purple mb-4">Alert settings</h2>

            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              {["telegram", "webpush", "termii"].map((name) => (
                <div key={name} className="border border-sapore-border rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold capitalize text-sapore-ink">{name === "webpush" ? "Web Push" : name}</span>
                    <input data-testid={`channel-toggle-${name}`} type="checkbox" className="w-5 h-5 accent-sapore-purple" checked={!!settings.channels?.[name]?.enabled} onChange={() => toggleChannel(name)} />
                  </div>
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${settings.configured?.[name] ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {settings.configured?.[name] ? "Configured" : "Not configured"}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <button data-testid="toggle-sound" onClick={() => saveSettings({ sound_enabled: !soundEnabled })} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-sapore-border">
                {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />} Sound {soundEnabled ? "on" : "off"}
              </button>
              <button data-testid="test-sound" onClick={playBeep} className="text-sm px-4 py-2 rounded-full border border-sapore-border">Test sound</button>
              <label className="text-sm text-sapore-inkSoft flex items-center gap-2">
                Repeat every
                <select data-testid="repeat-select" value={repeatSeconds} onChange={(e) => saveSettings({ repeat_seconds: parseInt(e.target.value) })} className="rounded-lg border border-sapore-border p-2 text-sm">
                  {[30, 45, 60, 90].map((s) => <option key={s} value={s}>{s}s</option>)}
                </select>
              </label>
              <button data-testid="send-test-alert" onClick={sendTest} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-sapore-gold text-sapore-ink font-semibold">
                <Send size={15} /> Send test alert
              </button>
            </div>

            {testResults && (
              <div className="bg-sapore-creamSurface rounded-xl p-4 mb-6 text-sm" data-testid="test-results">
                {testResults === "loading" ? "Sending…" : testResults.map((r, i) => (
                  <div key={`${r.driver}-${i}`} className="flex items-center justify-between border-b border-sapore-border/60 py-1.5">
                    <span className="font-medium capitalize">{r.driver}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "sent" ? "bg-green-100 text-green-700" : r.status === "skipped" ? "bg-gray-100 text-gray-500" : "bg-red-100 text-red-700"}`}>{r.status}{r.error ? ` — ${r.error}` : ""}</span>
                  </div>
                ))}
              </div>
            )}

            <h3 className="font-serif text-lg text-sapore-purple mb-2">Recent delivery log</h3>
            <div className="max-h-64 overflow-y-auto text-sm" data-testid="notification-log">
              {log.length === 0 ? <p className="text-sapore-muted">No notifications yet.</p> : log.map((r, i) => (
                <div key={`${r.driver}-${r.created_at}-${i}`} className="flex items-center justify-between border-b border-sapore-border/60 py-1.5">
                  <span className="capitalize">{r.driver}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "sent" ? "bg-green-100 text-green-700" : r.status === "skipped" ? "bg-gray-100 text-gray-500" : "bg-red-100 text-red-700"}`}>{r.status}</span>
                  <span className="text-xs text-sapore-muted">{new Date(r.created_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderColumn({ title, count, orders, onAck, showAck, accent, muted, testid }) {
  return (
    <div data-testid={testid}>
      <h2 className={`font-serif text-xl font-bold mb-3 ${accent ? "text-red-600" : "text-sapore-purple"}`}>{title} <span className="text-sapore-muted">({count})</span></h2>
      <div className="space-y-3">
        {orders.length === 0 && <p className="text-sapore-muted text-sm">Nothing here.</p>}
        {orders.map((o) => (
          <div key={o.id || o.reference} data-testid={`alert-order-${o.reference}`} className={`bg-white rounded-xl border overflow-hidden ${accent ? "border-l-4 border-l-sapore-purple border-sapore-border animate-pulse" : "border-sapore-border"} ${muted ? "opacity-70" : ""}`}>
            {o.allergy_flag ? (
              <div className="bg-red-600 text-white px-4 py-2" data-testid={`allergy-banner-${o.reference}`}>
                <p className="font-bold text-sm">⚠ ALLERGY — {(o.allergy_tags || []).join(", ")}</p>
                {o.allergy_note && <p className="text-xs text-white/90 mt-0.5">{o.allergy_note}</p>}
              </div>
            ) : null}
            <div className="p-4">
            {!o.allergy_flag && <p className="text-xs text-sapore-muted mb-1">No allergies noted.</p>}
            <div className="flex items-center justify-between">
              <span className="font-bold text-sapore-ink">{o.reference}</span>
              <span className="text-xs text-sapore-muted">{minutesAgo(o.created_at)}</span>
            </div>
            <p className="text-sm text-sapore-inkSoft mt-1">{o.customer?.name} · <a href={`tel:${o.customer?.phone}`} className="text-sapore-purple underline">{o.customer?.phone}</a></p>
            <p className="text-sm text-sapore-muted">{o.customer?.address}</p>
            <ul className="text-sm mt-2 space-y-0.5">
              {(o.items || []).map((it, i) => <li key={`${it.name}-${i}`}>{it.quantity}× {it.name}{it.variant ? ` (${it.variant})` : ""}</li>)}
            </ul>
            <div className="flex items-center justify-between mt-3">
              <span className="font-bold text-sapore-gold">{formatKobo(o.total)}</span>
              {showAck && (
                <button data-testid={`acknowledge-${o.reference}`} onClick={() => onAck(o)} className="inline-flex items-center gap-1.5 bg-sapore-purple text-sapore-cream font-semibold px-5 py-2.5 rounded-full hover:bg-sapore-purpleDark" style={{ minHeight: 44 }}>
                  <Check size={16} /> {o.allergy_flag ? "Acknowledge — allergy noted" : "Acknowledge"}
                </button>
              )}
            </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
